import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { blogPosts } from "@/data/blogPosts";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CalendarDays, Clock } from "lucide-react";

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  // Simple markdown-like rendering: split by ## headings
  const renderContent = (content: string) => {
    const sections = content.split(/^## /m);
    return sections.map((section, i) => {
      if (i === 0) {
        return (
          <p key={i} className="text-foreground/80 leading-relaxed text-lg mb-6">
            {section.trim()}
          </p>
        );
      }
      const [heading, ...rest] = section.split("\n");
      return (
        <div key={i} className="mb-6">
          <h2 className="font-display text-xl font-semibold mb-3">{heading.trim()}</h2>
          {rest
            .join("\n")
            .trim()
            .split("\n\n")
            .map((para, j) => (
              <p
                key={j}
                className="text-foreground/80 leading-relaxed mb-3"
                dangerouslySetInnerHTML={{
                  __html: para
                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                    .replace(/\*(.*?)\*/g, "<em>$1</em>")
                    .replace(/^- (.*)/gm, "<li class='ml-4 list-disc'>$1</li>"),
                }}
              />
            ))}
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <article className="container mx-auto px-6 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <Badge
              variant={post.category === "product" ? "default" : "secondary"}
              className="mb-4"
            >
              {post.category === "product" ? "Product" : "Travel"}
            </Badge>

            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {post.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">{renderContent(post.content)}</div>
          </motion.div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
